import { ToastContainer, toast } from "react-toastify";

import PropTypes from "prop-types";
import React from "react";

// Default configuration for toasts
const defaultToastConfig = {
  position: "bottom-right",
  autoClose: 5000,
  closeOnClick: true,
};
/**
 * NucToast component to display a toast notification.
 * @param {Object} props - Configuration options for the toast.
 * @param {strin|React.ReactNodeg} props.message - The message to display in the toast.
 * @param {string} [props.type] - Type of the toast ('info', 'success', 'warning', 'error').
 * @param {string} [props.toastId] - Id that stops a second toast with the same id from showing while the first is visible.
 * @param {string} [props.position] - Position of the toast on the screen.
 * @param {boolean} [props.hideProgressBar] - Whether to hide the progress bar.
 * @param {boolean} [props.pauseOnHover] - Whether to pause the toast on hover.
 * @param {boolean} [props.pauseOnFocusLoss] - Whether to pause the toast on focus loss.
 * @param {boolean} [props.closeOnClick] - Whether to close the toast on click.
 * @param {boolean} [props.draggable] - Whether the toast is draggable.
 * @param {boolean} [props.closeButton] - Whether to show a close button.
 * @param {number} [props.autoClose] - Time in milliseconds before the toast closes.
 * @param {string} [props.className] - Class name for the toast.
 */
export const NucToast = (props) => {
  const config = { ...defaultToastConfig, ...props };
  return toast(config?.message, {
    toastId: config?.toastId,
    type: config?.type,
    position: config?.position,
    onOpen: config?.onOpen,
    autoClose: config?.autoClose,
    onClose: config?.onClose,
    closeButton: config?.closeButton,
    transition: config?.transition,
    hideProgressBar: config?.hideProgressBar,
    pauseOnHover: config?.pauseOnHover,
    pauseOnFocusLoss: config?.pauseOnFocusLoss,
    closeOnClick: config?.closeOnClick,
    className: config?.className,
    bodyClassName: config?.bodyClassName,
    style: config?.style,
    progressClassName: config?.progressClassName,
    progressStyle: config?.progressStyle,
    draggable: config?.draggable,
    draggablePercent: config?.draggablePercent,
    draggableDirection: config?.draggableDirection,
    role: config?.role,
    delay: config?.delay,
    onClick: config?.onClick,
    render: config?.render,
    isLoading: config?.isLoading,
    data: config?.data,
    theme: config?.theme,
  });
};

export const NucToastContainer = (props) => {
  return (
    <ToastContainer
      position={props?.position}
      autoClose={props?.autoClose ?? 5000} // Default to 5 seconds if not provided
      closeButton={props?.closeButton}
      transition={props?.transition}
      hideProgressBar={props?.hideProgressBar ?? false}
      pauseOnHover={props?.pauseOnHover}
      pauseOnFocusLoss={props?.pauseOnFocusLoss}
      rtl={props?.rtl}
      closeOnClick={props?.closeOnClick}
      newestOnTop={props?.newestOnTop}
      className={props?.className}
      style={props?.style}
      toastClassName={props?.toastClassName}
      bodyClassName={props?.bodyClassName}
      progressClassName={props?.progressClassName}
      progressStyle={props?.progressStyle}
      draggable={props?.draggable}
      draggablePercent={props?.draggablePercent}
      draggableDirection={props?.draggableDirection}
      enableMultiContainer={props?.enableMultiContainer}
      containerId={props?.containerId}
      limit={props?.limit}
      role={props?.role}
      theme={props?.theme}
    />
  );
};

NucToastContainer.defaultProps = {
  position: "bottom-left",
  autoClose: 200000,
  closeButton: false,
};

NucToastContainer.propTypes = {
  position: PropTypes.string,
  autoClose: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  closeButton: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  type: PropTypes.oneOf(["info", "success", "warning", "error"]),
  transition: PropTypes.node,
  hideProgressBar: PropTypes.bool,
  pauseOnHover: PropTypes.bool,
  pauseOnFocusLoss: PropTypes.bool,
  rtl: PropTypes.bool,
  closeOnClick: PropTypes.bool,
  newestOnTop: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  toastClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
  progressClassName: PropTypes.string,
  progressStyle: PropTypes.object,
  draggable: PropTypes.bool,
  draggablePercent: PropTypes.number,
  draggableDirection: PropTypes.string,
  enableMultiContainer: PropTypes.bool,
  containerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  limit: PropTypes.number,
  role: PropTypes.string,
  theme: PropTypes.string,
};
